'use client';
import React, { useEffect, useState } from 'react';
import { TrackingManager, AuthEventType } from '@benzinga/tracking-manager';
import { Authentication, AuthenticationManager, AuthMode } from '@benzinga/session';
import { SessionContext } from '@benzinga/session-context';
import { SafeType } from '@benzinga/safe-await';

import { UserManager } from '@benzinga/user-manager';
import { AccountCreationIterations } from '../AuthIterations/AccountCreationIterations';
import { DefaultIteration } from '../AuthIterations/DefaultIteration';
import { AuthIterationType } from '../AuthIterations/types';
import { formatRedirectUrl, preventRedirectToLogin } from '../AuthIterations/utils';

import { HOST_LOCALE_MAPPING } from '@benzinga/translate';
import { NotificationManager } from '@benzinga/notification-manager';
import { SophiIterations } from '../AuthIterations/SophiIterations';
import { disableFreestarStickyFooter } from '@benzinga/ads-utils';
import { useSearchParams } from '../../hooks/useSearchParams';

const subdomainsToRedirect = ['pro', 'probeta', ...Object.keys(HOST_LOCALE_MAPPING)];

export const AuthContainer = ({
  authMode = 'login',
  contentType,
  email,
  iterationStyle = 'default',
  iterationVersion,
  nextUrl,
  onLogin,
  onRegister,
  onSessionLoad,
  placement,
  preventRedirect,
  setShowPaywall,
}: AuthIterationType) => {
  const session = React.useContext(SessionContext);
  const [mounted, setMounted] = useState(false);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [iframe, setIframe] = useState<boolean>(false);
  const [mode, setMode] = useState<AuthMode | string | string[]>(authMode);
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(undefined);
  const searchParams = useSearchParams();
  const isPaywalled =
    iterationStyle === 'default' ? searchParams.get('is_paywall') === 'true' : iterationStyle !== 'account-creation';
  const isRegwalled = searchParams.get('is_regwall') === 'true' || iterationStyle === 'account-creation';

  useEffect(() => {
    const iframeParam = searchParams.get('iframe');
    if (iframeParam === 'true') {
      setIframe(true);
    }
  }, [searchParams]);

  useEffect(() => {
    session.getManager(TrackingManager).updateContext({ isPaywalled: isPaywalled, isRegwalled: isRegwalled });
    return () => {
      session.getManager(TrackingManager).updateContext({ isPaywalled: false, isRegwalled: false });
    };
  }, [session, isPaywalled, isRegwalled]);

  const getRedirectUrl = React.useCallback(
    async (forceUrl?: string, isSocial?: boolean): Promise<string> => {
      // check if user has completed onboarding
      const userManager = session.getManager(UserManager);
      const user = await userManager.getUser();
      const localization = user?.profileData?.subdomain ?? null;

      const domain = window?.location?.hostname?.match(/[^.]+\.[^.]+$/)?.[0] || 'benzinga.com';
      const sub = window.location.hostname.split('.')[0];
      const subdomain = subdomainsToRedirect.includes(sub) ? sub : 'www';

      const defaultUrl = `https://www.${domain}/profile/portfolio/`;

      const referrer = document.referrer;

      let parentUrl;

      try {
        parentUrl =
          window.location.href !== window?.parent?.location?.href ? window?.parent?.location?.href : undefined;
      } catch (_e) {
        // use document.referrer for cross domain scenarios ex. pro.benzinga.com
        parentUrl = document.referrer;
      }

      // redirect after register
      const registerNextParam = searchParams.get('registernext');
      const registerNext = Array.isArray(registerNextParam) ? registerNextParam[0] : registerNextParam;

      // redirect after login
      const loginNextParam = searchParams.get('loginnext');
      const loginNext = Array.isArray(loginNextParam) ? loginNextParam[0] : loginNextParam;

      // redirect after login or register
      const redirectNextParam = searchParams.get('next') || searchParams.get('redirect');
      const redirectNext = Array.isArray(redirectNextParam) ? redirectNextParam[0] : redirectNextParam;

      let redirectUrl =
        forceUrl || // force a specific url
        (mode === 'login' ? loginNext : registerNext) || // try login or register next depending on auth mode
        redirectNext || // try general next
        nextUrl || // try nextUrl prop
        referrer || // try referrer
        parentUrl || // try iframe parent url
        defaultUrl; // fallback to default url

      // cleanup redirect url
      redirectUrl = formatRedirectUrl(redirectUrl, defaultUrl);
      redirectUrl = preventRedirectToLogin(redirectUrl, defaultUrl);

      if (isSocial) {
        // if is social auth, no need to attach welcome until user is redirected back
        redirectUrl = `https://${subdomain}.${domain}/login?social=success&is_paywall=${isPaywalled ? 'true' : 'false'}&is_regwall=${isRegwalled ? 'true' : 'false'}&next=${encodeURIComponent(redirectUrl)}&iframe=${iframe}`;
      }
      if (localization && localization !== 'benzinga' && localization !== 'accounts') {
        redirectUrl = `https://${localization}.benzinga.com`;
      }

      return redirectUrl;
    },
    [mode, searchParams, session, iframe, nextUrl, isPaywalled, isRegwalled],
  );

  const onSocialClicked = React.useCallback(
    async (socialPath: string, forceUrl?: string) => {
      // get the redirect url
      let redirectUrl = await getRedirectUrl(forceUrl, true);

      // url encode the redirect url
      redirectUrl = encodeURIComponent(redirectUrl);

      // open the social auth url and pass along the redirect url
      window.open(
        `https://accounts.benzinga.com/oauth/login/${socialPath}/?embedded=${
          iframe ? 'true' : 'false'
        }&next=${redirectUrl}`,
        '_top',
      );
    },
    [getRedirectUrl, iframe],
  );

  const handleSuccessfulAuth = React.useCallback(
    async (authSession: SafeType<Authentication>, type: string, backendFallback?: AuthEventType) => {
      // get the redirect url
      const redirectUrl = await getRedirectUrl();

      // call onSessionLoad callback
      onSessionLoad && onSessionLoad(authSession);
      setPhoneNumber(authSession?.ok?.user?.phoneNumber);

      if (preventRedirect || authMode === 'sms-verify' || authMode === 'complete') {
        return;
      }

      const isSocial = searchParams?.get('social') === 'success';
      const backend = searchParams?.get('backend');

      const backendType =
        backend === 'google-oauth2'
          ? 'google'
          : backend === 'google-one-tap'
            ? 'google-one-tap'
            : backend === 'microsoft-graph'
              ? 'microsoft'
              : backend === 'apple-id'
                ? 'apple'
                : backendFallback || (backend as AuthEventType);

      if (backendType || isSocial) {
        await session.getManager(TrackingManager).trackAuthEvent(type === 'register' ? 'register' : 'login', {
          auth_type: backendType ?? 'social-fallback',
          is_paywalled: isPaywalled,
          is_regwalled: isRegwalled,
        });
      }

      if (iframe) {
        // if within an iframe, we'll send a message to the parent window
        console.log('sending message to parent window', {
          next: redirectUrl,
          type: `${type}_succeed`,
          user: authSession?.ok?.user,
        });
        window.parent.postMessage({ next: redirectUrl, type: `${type}_succeed`, user: authSession?.ok?.user }, '*');
      } else {
        if ('Notification' in window && Notification.permission === 'granted') {
          await session.getManager(NotificationManager).registerPushDevice();
        }
        // if not in an iframe we'll redirect the user
        window.open(redirectUrl, '_top');
      }
    },
    [authMode, getRedirectUrl, onSessionLoad, iframe, preventRedirect, session, searchParams, isPaywalled, isRegwalled],
  );

  const onLoginSuccess = React.useCallback(
    async (auth: SafeType<Authentication>, authFor: string) => {
      await handleSuccessfulAuth(auth, authFor);
      if (onLogin) {
        await onLogin(auth, authFor);
      }
    },
    [handleSuccessfulAuth, onLogin],
  );

  const onRegisterSuccess = React.useCallback(
    async (auth: SafeType<Authentication>, authFor: string) => {
      await handleSuccessfulAuth(auth, authFor);
      if (onRegister) {
        await onRegister(auth, authFor);
      }
    },
    [handleSuccessfulAuth, onRegister],
  );

  const getSession = React.useCallback(async () => {
    if (!isLoadingSession) {
      setIsLoadingSession(true);

      // pass in true to force a refresh of the session, otherwise it'll keep reference to the anonymous user
      const authSession = await session.getManager(AuthenticationManager).getAuthSession(true);

      if (authSession.err) {
        // console.error(authSession.err);
      } else if (authSession.ok?.user?.accessType !== 'anonymous') {
        const newUser = searchParams?.get('new_user');
        const isSocialRedirect = newUser !== null && newUser !== undefined && newUser !== 'undefined';
        await session.getManager(TrackingManager).setUser(authSession.ok?.user);
        await handleSuccessfulAuth(
          authSession,
          newUser === 'False' ? 'login' : 'register',
          isSocialRedirect ? 'social-fallback' : undefined,
        );
      }

      setIsLoadingSession(false);
    }
  }, [handleSuccessfulAuth, isLoadingSession, session, searchParams]);

  useEffect(() => {
    disableFreestarStickyFooter();
    setMounted(true);
    if (!mounted) {
      getSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!mounted) {
    return null;
  }

  if (iterationStyle === 'account-creation') {
    return (
      <AccountCreationIterations
        allowClose={false}
        authMode={mode}
        contentType={contentType}
        email={email}
        iterationVersion={iterationVersion}
        onLogin={onLoginSuccess}
        onRegister={onRegisterSuccess}
        onSessionLoad={onSessionLoad}
        onSocialClicked={onSocialClicked}
        phoneNumber={phoneNumber}
        placement={placement}
        setAuthMode={setMode}
      />
    );
  } else if (iterationStyle === 'sophi') {
    return (
      <SophiIterations
        allowClose={true}
        authMode={mode}
        contentType={contentType}
        placement={placement}
        setShowPaywall={setShowPaywall}
      />
    );
  }

  return (
    <DefaultIteration
      allowClose={false}
      authMode={mode}
      email={email}
      isPaywalled={isPaywalled}
      isRegwalled={isRegwalled}
      onLogin={onLoginSuccess}
      onRegister={onRegisterSuccess}
      onSessionLoad={onSessionLoad}
      onSocialClicked={onSocialClicked}
      phoneNumber={phoneNumber}
      placement={placement}
      setAuthMode={setMode}
    />
  );
};
